/**
 * Goal type selector — numeric vs steps mode + steps builder.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Hash, ListChecks, Plus, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { GoalStep } from '@turnifly/shared/types'

interface GoalFormTypeProps {
  goalType: 'numeric' | 'steps'
  setGoalType: (type: 'numeric' | 'steps') => void
  steps: GoalStep[]
  setSteps: (steps: GoalStep[]) => void
}

export function GoalFormType({
  goalType,
  setGoalType,
  steps,
  setSteps,
}: GoalFormTypeProps): React.ReactElement {
  const { t } = useTranslation()

  const handleAddStep = useCallback((): void => {
    if (steps.length >= 20) return
    setSteps([...steps, { label: '' }])
  }, [steps, setSteps])

  const handleRemoveStep = useCallback(
    (index: number): void => {
      setSteps(steps.filter((_, i) => i !== index))
    },
    [steps, setSteps],
  )

  const handleStepChange = useCallback(
    (index: number, label: string): void => {
      const updated = steps.map((s, i) => (i === index ? { label } : s))
      setSteps(updated)
    },
    [steps, setSteps],
  )

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-white/70">
        {t('goals.goalType')}
      </label>

      {/* Type selector */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setGoalType('numeric')}
          className={`flex items-center gap-2 rounded-xl border p-3 text-left transition-colors ${
            goalType === 'numeric'
              ? 'border-primary bg-primary/10 text-white'
              : 'border-white/10 text-white/50'
          }`}
        >
          <Hash className="h-5 w-5 shrink-0" />
          <div>
            <div className="text-sm font-medium">{t('goals.goalTypeNumeric')}</div>
            <div className="text-xs text-white/40">{t('goals.goalTypeNumericDesc')}</div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setGoalType('steps')}
          className={`flex items-center gap-2 rounded-xl border p-3 text-left transition-colors ${
            goalType === 'steps'
              ? 'border-primary bg-primary/10 text-white'
              : 'border-white/10 text-white/50'
          }`}
        >
          <ListChecks className="h-5 w-5 shrink-0" />
          <div>
            <div className="text-sm font-medium">{t('goals.goalTypeSteps')}</div>
            <div className="text-xs text-white/40">{t('goals.goalTypeStepsDesc')}</div>
          </div>
        </button>
      </div>

      {/* Steps builder (only for steps mode) */}
      <AnimatePresence>
        {goalType === 'steps' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2 overflow-hidden"
          >
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white/70">
                {t('goals.stepsLabel')} ({steps.length})
              </label>
              <button
                type="button"
                onClick={handleAddStep}
                disabled={steps.length >= 20}
                className="flex items-center gap-1 rounded-lg bg-primary/20 px-2.5 py-1 text-xs font-medium text-primary disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('goals.addStep')}
              </button>
            </div>

            <div className="space-y-1.5">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  className="flex items-center gap-2"
                >
                  <span className="shrink-0 text-xs text-white/20">●</span>
                  <span className="shrink-0 text-xs text-white/30">{index + 1}</span>
                  <input
                    type="text"
                    value={step.label}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                    placeholder={t('goals.stepPlaceholder')}
                    maxLength={200}
                    className="flex-1 rounded-xl border border-white/[0.08] bg-white/[0.06] px-3 py-2 text-sm text-text placeholder:text-text-hint/40 outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveStep(index)}
                    className="shrink-0 rounded-lg p-1.5 text-white/30 hover:bg-white/10 hover:text-danger"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </motion.div>
              ))}
            </div>

            {steps.length === 0 && (
              <p className="py-4 text-center text-sm text-white/30">
                {t('goals.addStep')}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
